import { useGetSettingsBasicPublic } from '@agos/api-client/platform'
import { useGetNotificationsUnreadCount } from '@agos/api-client/notifications'
import {
  LANGUAGE_TO_COUNTRY,
  offeredLanguages,
  T,
  getCurrentLanguage,
  langInfo,
  setLanguage,
  tt,
  useAuth,
  useIsMobile,
  useTheme,
  roleLabel,
  surfacesFor,
} from '@agos/shared'
import { Spinner, cn } from '@agos/ui'
import { Bell, Check, ChevronDown, ListChecks, LogOut, Menu, PanelLeftClose, PanelLeftOpen, Receipt, Truck, User, Utensils, Wine, X } from 'lucide-react'
import { useState, type ReactNode } from 'react'
import { Link, useLocation } from 'wouter'

import { NAV_SECTIONS, type NavGuard } from '../generated/nav-tree'
import type { RouteGuard } from '../module-contract'
import { SidebarAttendance } from '@agos/manager-hr'
import { AGOS_NAV_SECTIONS } from './agos-nav'
import type { LucideIcon } from 'lucide-react'
import { FranchiseSwitcher } from './FranchiseSwitcher'
import { canAccess, useAccessContext, type AccessContext } from './access'
import { navItemVisible, routeGuardsByPath } from './nav-visibility'
import { badgeText, maySeeNotifications, unreadOf } from './notification-bell'
import { composedRoutes } from '../module-registry'

/**
 * What the router will actually enforce, by path — §9.224.
 *
 * Built once: `composedRoutes()` reads static module arrays, so this cannot change between renders,
 * and rebuilding a 57-entry map on every sidebar render would be work for nothing.
 */
const ROUTE_GUARDS = routeGuardsByPath(composedRoutes())

/**
 * The chrome, ported from legacy `components/layout.tsx`: a dark sidebar of
 * nine sections, a header carrying the franchise switcher and the user menu,
 * and the page in the middle.
 *
 * The sidebar is a drawer below 768 px because kitchen and POS run on tablets
 * (§14.9). Collapsible sections keep their open state per section key, as
 * legacy does, so a dispatcher who lives in one section is not re-opening it
 * every shift.
 */

/**
 * A section the sidebar can draw — legacy's, or one of §9.30's.
 *
 * The two differ only in the guard each item carries: legacy's are `NavGuard` (permission and tier),
 * the AGOS pages' are `RouteGuard` (which adds `systemRoles`). `canAccess` has taken either since
 * §9.7 gave the sidebar and the router one evaluator, so widening the prop is all that is needed —
 * and the generated file stays generated.
 */
interface DrawableSection {
  heading: string
  sectionKey?: string | null
  collapsible?: boolean
  guard?: NavGuard | RouteGuard | undefined
  items: ReadonlyArray<{
    label: string
    path: string
    icon: LucideIcon | null
    guard: NavGuard | RouteGuard | undefined
  }>
}

/**
 * An AGOS section that legacy has no counterpart for, so it draws on its own — §9.112.
 *
 * <p>A section whose heading matches one of legacy's is **merged into it** instead. Drawing a second
 * block called *Správa rozvoz* under the first would read as a bug; the owner asked for the driver app
 * to sit *"na lištu k správe rozvozu"*, which is that section and not one beside it.
 */
function isItsOwnSection(section: { heading: string }): boolean {
  return !NAV_SECTIONS.some((generated) => generated.heading === section.heading)
}

/** Legacy's section, plus any AGOS items that belong in it. */
function withAgosExtras(section: DrawableSection): DrawableSection {
  const extras = AGOS_NAV_SECTIONS.filter((agos) => agos.heading === section.heading).flatMap(
    (agos) => agos.items,
  )
  return extras.length === 0 ? section : { ...section, items: [...section.items, ...extras] }
}

/**
 * Whether the sidebar was folded away last time - §9.368.
 *
 * **Wrapped, because a private window throws on access rather than returning null**, and a shell
 * that failed to render because it could not read a preference would be a far worse bug than the one
 * this setting exists for. An unreadable store means *not collapsed*, which is the state the app has
 * always had.
 */
const COLLAPSED_KEY = 'agos.sidebar-collapsed'

function readCollapsed(): boolean {
  try {
    return localStorage.getItem(COLLAPSED_KEY) === '1'
  } catch {
    return false
  }
}

function writeCollapsed(collapsed: boolean): void {
  try {
    localStorage.setItem(COLLAPSED_KEY, collapsed ? '1' : '0')
  } catch {
    // Nothing to do and nothing to say: the panel still folds, it just will not be remembered.
  }
}

function SidebarSection({
  section,
  access,
  onNavigate,
}: {
  section: DrawableSection
  access: AccessContext
  onNavigate: () => void
}) {
  const [location] = useLocation()
  /**
   * Closed until opened — §9.7.1, and a knowing departure from legacy.
   *
   * Legacy opens all nine at once, and this file copied that on purpose. On a tablet, which is where
   * this app runs, that is a wall of links to scroll past before reaching anything. The open state is
   * still remembered per section, so the original reasoning — *a dispatcher is not re-opening their
   * section every shift* — survives; it is only the starting state that changes.
   *
   * The section holding the current page opens itself, or somebody who followed a link would find the
   * sidebar disagreeing with the screen.
   */
  const [collapsed, setCollapsed] = useState(
    () => !section.items.some((item) => item.path === window.location.pathname),
  )

  if (!canAccess(section.guard, access)) return null

  // §9.224 — the router's guard as well as the sidebar's own, so a link cannot be offered that
  // `RouteGate` would answer with the lock panel.
  const visibleItems = section.items.filter((item) => navItemVisible(item, ROUTE_GUARDS, access))
  // A section whose every item is gated away is not rendered as an empty
  // heading — legacy hides the whole block.
  if (visibleItems.length === 0) return null

  const isOpen = section.collapsible !== true || !collapsed

  return (
    <div className="mb-1">
      {section.collapsible === true ? (
        <button
          type="button"
          onClick={() => setCollapsed((value) => !value)}
          // A control that folds something has to say whether it is folded. Without this the only
          // statement of the section's state was the chevron's rotation, which a screen reader cannot
          // read and a test cannot ask.
          aria-expanded={isOpen}
          data-nav-section={section.heading}
          className="w-full flex items-center justify-between px-4 py-2 text-[10px] font-bold uppercase tracking-wider text-sidebar-foreground/40 hover:text-sidebar-foreground/70 transition-colors"
        >
          <T>{section.heading}</T>
          <ChevronDown className={cn('w-3 h-3 transition-transform', !isOpen && '-rotate-90')} />
        </button>
      ) : (
        <p className="px-4 py-2 text-[10px] font-bold uppercase tracking-wider text-sidebar-foreground/40">
          <T>{section.heading}</T>
        </p>
      )}

      <div className={cn('collapsible-section', isOpen && 'open')}>
        <div>
          {visibleItems.map((item) => {
            const Icon = item.icon
            const isActive = location === item.path
            return (
              <Link
                key={item.path}
                href={item.path}
                onClick={onNavigate}
                className={cn(
                  'flex items-center gap-3 px-4 py-2 text-sm transition-colors',
                  isActive
                    ? 'bg-sidebar-primary/15 text-sidebar-primary font-semibold border-r-2 border-sidebar-primary'
                    : 'text-sidebar-foreground/70 hover:text-sidebar-foreground hover:bg-white/5',
                )}
              >
                {Icon && <Icon className="w-4 h-4 shrink-0" />}
                <span className="truncate">
                  <T>{item.label}</T>
                </span>
              </Link>
            )
          })}
        </div>
      </div>
    </div>
  )
}

/**
 * The bell, left of the language control - FRONTEND.md §9.348, the owner's C4.
 *
 * **A missed port rather than a new feature.** Legacy's `layout.tsx:231` is a `NotificationBell` and
 * its lines 582-583 render it immediately before the language switcher, which is exactly where the
 * owner asked for it: *"in the top right corner left to the language selector"*. AGOS ported the page
 * and the unread endpoint and left the bell behind.
 *
 * **Sixty seconds, as legacy polls.** The count is a badge somebody glances at, not a figure anything
 * is computed from, so a minute late is right and a socket would be a new dependency for it.
 *
 * No pulse animation, which legacy has: this sits beside a static language flag all day, and a
 * permanent animation next to it reads as a fault rather than as news.
 */
function NotificationBell() {
  const { user } = useAuth()
  const maySee = maySeeNotifications(user?.systemRole)
  const { data } = useGetNotificationsUnreadCount({
    query: { refetchInterval: 60_000, enabled: maySee },
  })
  if (!maySee) return null
  const badge = badgeText(unreadOf(data))

  return (
    <Link
      href="/notifications"
      data-notification-bell
      aria-label={tt('Oznámenia')}
      title={tt('Oznámenia')}
      className="relative flex items-center p-2 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
    >
      <Bell className="w-5 h-5" />
      {badge !== '' && (
        <span
          data-notification-badge
          className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 bg-destructive text-destructive-foreground text-[10px] font-bold rounded-full flex items-center justify-center"
        >
          {badge}
        </span>
      )}
    </Link>
  )
}

/**
 * The language the till is run in — §9.11.
 *
 * The catalogue, the runtime and the `fk_lang` key are `@agos/shared`'s, so this and the customer app
 * offer the same list and write the same preference: one browser, one choice, whether the person is
 * taking an order or placing one.
 *
 * **Only the locales with bundles.** Twelve languages are catalogued and seven ship translations;
 * offering the other five would switch the app into a language it then falls back to Slovak for, which
 * reads as the switcher being broken rather than as the translation being absent.
 */
function LanguageMenu() {
  // §9.92 — only the languages the franchise turned on in *AI preklad*. `basic-public` was built to
  // answer exactly this and nothing read it; `offeredLanguages` keeps the default and falls back to
  // every shipped bundle when the call has not answered, so a slow read never shrinks the menu.
  const { data: languageSettings } = useGetSettingsBasicPublic()
  const offered = offeredLanguages(
    (languageSettings as { autoTranslateLanguages?: string[] } | undefined)?.autoTranslateLanguages,
    (languageSettings as { defaultLanguage?: string } | undefined)?.defaultLanguage,
  )

  const [open, setOpen] = useState(false)
  const current = getCurrentLanguage()

  return (
    <div className="relative">
      <button
        type="button"
        data-language-open
        onClick={() => setOpen((value) => !value)}
        // *Zmeniť jazyk*, not *Jazyk*: `/settings/translations` has its own **Jazyk** select, and two
        // controls answering to one accessible name is ambiguous to a screen reader before it is
        // ambiguous to a test.
        aria-label={tt('Zmeniť jazyk')}
        className="flex items-center gap-1.5 p-2 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
      >
        <img
          src={`/flags/${LANGUAGE_TO_COUNTRY[current] ?? 'sk'}.svg`}
          alt=""
          className="w-5 h-auto rounded-[2px]"
        />
        <span className="text-xs font-bold uppercase">{current}</span>
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div
            data-language-menu
            className="absolute right-0 top-full mt-1 z-50 w-44 rounded-xl border border-border bg-card shadow-xl py-1"
          >
            {offered.map((code) => (
              <button
                key={code}
                type="button"
                data-language={code}
                onClick={() => {
                  setLanguage(code)
                  setOpen(false)
                }}
                className="w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-muted transition-colors"
              >
                <img
                  src={`/flags/${LANGUAGE_TO_COUNTRY[code] ?? code}.svg`}
                  alt=""
                  className="w-5 h-auto rounded-[2px]"
                />
                <span className="flex-1 text-left">{langInfo(code).label}</span>
                {code === current && <Check className="w-4 h-4 text-primary" />}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  )
}

/**
 * The three places the owner reaches for often enough to want them one press away — §9.11.
 *
 * Each carries the same guard as its own sidebar entry. A shortcut to a page somebody cannot open is
 * worse than no shortcut: it looks like a permission problem rather than a design one.
 */
const HEADER_SHORTCUTS = [
  {
    path: '/lists',
    label: tt('Zoznamy & Archív'),
    icon: ListChecks,
    // §9.31 — the hue follows the thing, not the position, so a shortcut keeps its colour if the
    // order ever changes. Written out rather than composed: Tailwind reads these at build time and a
    // `text-${hue}-500` would be purged.
    tone: 'text-sky-500 hover:text-sky-400 hover:bg-sky-500/10',
    guard: { permission: 'lists' },
  },
  {
    path: '/beverage-stock',
    label: tt('Nápojový sklad'),
    icon: Wine,
    tone: 'text-violet-500 hover:text-violet-400 hover:bg-violet-500/10',
    guard: { permission: 'beverage_stock', franchiseModule: 'stock.beverage' },
  },
  {
    path: '/fiskal-closures',
    label: tt('Pokladňa - uzávierky'),
    icon: Receipt,
    tone: 'text-emerald-500 hover:text-emerald-400 hover:bg-emerald-500/10',
    guard: { permission: 'fiskal_closures_view', franchiseModule: 'fiskal' },
  },
] as const

export function AppLayout({ children }: { children: ReactNode }) {
  const access = useAccessContext()
  const { user, logout } = useAuth()
  const { restaurantName, logoUrl } = useTheme()
  const isMobile = useIsMobile()
  const [drawerOpen, setDrawerOpen] = useState(false)
  /**
   * The sidebar folded away - §9.368, the owner's *"make left panel that we can hide it, add button
   * next to title of the sidebar"*.
   *
   * **Remembered per browser**, because somebody who wants the room wants it on the next screen too,
   * and a till running all evening should not be re-folded every navigation. `localStorage` is the
   * right store for this and the wrong one for anything shared: it is one person's preference on one
   * device, which is exactly what it holds.
   *
   * **Desktop only.** On mobile the sidebar is already a drawer that closes on every navigation, so
   * a second way to hide it would fight the first.
   */
  const [collapsed, setCollapsed] = useState(() => readCollapsed())
  const hidden = !isMobile && collapsed

  const toggleCollapsed = () => {
    setCollapsed((current) => {
      const next = !current
      writeCollapsed(next)
      return next
    })
  }

  const sidebarVisible = (!isMobile || drawerOpen) && !hidden

  return (
    <div className="min-h-screen bg-background">
      {sidebarVisible && (
        <aside
          data-sidebar
          className={cn(
            'fixed inset-y-0 left-0 z-40 w-64 bg-sidebar border-r border-sidebar-border flex flex-col',
            isMobile && 'shadow-2xl',
          )}
        >
          {/* §9.7.2 - the brand no longer scrolls away: only the nav between these two moves. */}
          <div
            data-sidebar-brand
            className="shrink-0 flex items-center gap-2 px-4 h-14 border-b border-sidebar-border"
          >
            {logoUrl ? (
              <img src={logoUrl} alt={restaurantName} className="h-7 w-auto" />
            ) : (
              <span className="font-display font-bold text-sidebar-foreground truncate">
                {restaurantName}
              </span>
            )}
            {/* §9.368 - beside the title, where the owner asked for it. Desktop only: on mobile the
                drawer's own backdrop and every navigation already close it. */}
            {!isMobile && (
              <button
                type="button"
                data-sidebar-collapse
                aria-label={tt('Skryť bočný panel')}
                title={tt('Skryť bočný panel')}
                onClick={toggleCollapsed}
                className="ml-auto shrink-0 w-8 h-8 rounded-lg flex items-center justify-center text-sidebar-foreground/70 hover:text-sidebar-foreground hover:bg-white/5 transition-colors"
              >
                <PanelLeftClose className="w-4 h-4" />
              </button>
            )}
          </div>

          <nav className="sidebar-scroll flex-1 overflow-y-auto py-2">
            {NAV_SECTIONS.map((section) => (
              <SidebarSection
                key={section.heading}
                section={withAgosExtras(section)}
                access={access}
                onNavigate={() => setDrawerOpen(false)}
              />
            ))}
            {/* §9.30 - the pages legacy has no nav entry for because legacy has no such pages. Four
                screens with backends behind them and nothing linking to any of them, which is how
                *"Príplatky a Zálohy kompletne chýbajú"* was true of a feature that was built. */}
            {AGOS_NAV_SECTIONS.filter(isItsOwnSection).map((section) => (
              <SidebarSection
                key={section.heading}
                section={section}
                access={access}
                onNavigate={() => setDrawerOpen(false)}
              />
            ))}
          </nav>

          {/* §9.7.3–4 - the end of a shift, where somebody looks for it. Pinned for the same reason
              the brand is: a sidebar nine sections long scrolls, and signing out should not require
              finding the bottom of it. §9.32's punch sits here for that same reason, and above the
              divider: arriving and leaving is the first and last thing anybody does with this app. */}
          <div className="shrink-0 px-0 pb-1">
            <SidebarAttendance />
          </div>
          <div
            data-sidebar-footer
            className="shrink-0 border-t border-sidebar-border p-2 space-y-1"
          >
            <Link
              href="/profile"
              data-sidebar-profile
              onClick={() => setDrawerOpen(false)}
              className="flex items-center gap-3 px-2 py-2 rounded-lg text-sidebar-foreground/80 hover:text-sidebar-foreground hover:bg-white/5 transition-colors"
            >
              <span className="w-8 h-8 rounded-full bg-sidebar-primary/20 text-sidebar-primary flex items-center justify-center shrink-0">
                <User className="w-4 h-4" />
              </span>
              <span className="min-w-0 leading-tight">
                <span className="block text-sm font-semibold truncate">{user?.name}</span>
                <span className="block text-[11px] text-sidebar-foreground/50" data-user-role>
                  {/* §9.257 - this printed the key, so the owner read `main_admin` and a cook read
                    * `cook`, on every screen. The mapping already existed twice in the app and not
                    * here; it lives in `shared` now because the shell cannot import a module. */}
                  {roleLabel(user?.systemRole)}
                </span>
              </span>
            </Link>
            <button
              type="button"
              data-sidebar-logout
              onClick={() => void logout()}
              className="w-full flex items-center gap-3 px-2 py-2 rounded-lg text-sidebar-foreground/70 hover:text-destructive hover:bg-destructive/10 transition-colors"
            >
              <span className="w-8 h-8 flex items-center justify-center shrink-0">
                <LogOut className="w-4 h-4" />
              </span>
              <span className="text-sm font-semibold">
                <T>Odhlásiť sa</T>
              </span>
            </button>
          </div>
        </aside>
      )}

      {isMobile && drawerOpen && (
        <div className="fixed inset-0 z-30 bg-black/50" onClick={() => setDrawerOpen(false)} />
      )}

      {/* §9.368 - the way back, in the place the sidebar's own toggle was. Fixed so it is reachable
          from anywhere on a long page, and the only control on screen that can bring the panel back. */}
      {hidden && (
        <button
          type="button"
          data-sidebar-expand
          aria-label={tt('Zobraziť bočný panel')}
          title={tt('Zobraziť bočný panel')}
          onClick={toggleCollapsed}
          className="fixed top-3 left-3 z-40 w-9 h-9 rounded-lg bg-card border border-border shadow-sm flex items-center justify-center text-foreground/70 hover:text-foreground transition-colors"
        >
          <PanelLeftOpen className="w-4 h-4" />
        </button>
      )}

      {/* §9.403 - `data-app-content`, because the print stylesheet has to undo this margin. The
          sidebar is `display: none` when printing and this 16 rem was reserving its space anyway,
          so every printed page in the manager started 256 px from the left edge of the paper. A
          hook rather than a `.ml-64` match in the CSS: the width is a layout decision that will
          change, and `[data-sidebar]` in the same block is already there for the same reason. */}
      <div data-app-content className={cn(!isMobile && !hidden && 'ml-64')}>
        <header className="sticky top-0 z-20 flex items-center justify-between gap-3 h-14 px-4 bg-card/95 backdrop-blur border-b border-border">
          <div className="flex items-center gap-3 min-w-0">
            {isMobile && (
              <button
                type="button"
                onClick={() => setDrawerOpen((open) => !open)}
                aria-label="Menu"
                className="p-2 -ml-2 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
              >
                {drawerOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
              </button>
            )}
          </div>

          <div className="flex items-center gap-2">
            {/* §9.1: switchable in-app, without re-login. Absent for a role with one surface. */}
            {/* §9.112 - the three-way surface *switcher* was here and stays gone: a per-device
              * preference is a second answer to a question the permission system already answers.
              *
              * §9.162 — what returns is **navigation**, not preference. The owner asked for a way to
              * get from the manager to the driving surface *"with permissions"*; `surfacesFor` is
              * already the function that says who may, so the link asks it rather than inventing a
              * second rule, and nothing is remembered. A role with one surface sees nothing. */}
            {surfacesFor(user?.systemRole)
              .filter((option) => option.surface !== 'app')
              .map((option) => {
                const Icon = option.surface === 'driving' ? Truck : Utensils
                const label = option.surface === 'driving' ? tt('Rozvoz') : tt('Nástenka')
                return (
                  <Link
                    key={option.surface}
                    href={option.path}
                    data-surface-link={option.surface}
                    title={label}
                    aria-label={label}
                    className="p-2 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
                  >
                    <Icon className="w-4 h-4" />
                  </Link>
                )
              })}
            <FranchiseSwitcher />
            {/* §9.11 - one press away, each behind its own page's guard. */}
            {HEADER_SHORTCUTS.filter((shortcut) => navItemVisible(shortcut, ROUTE_GUARDS, access)).map(
              (shortcut) => {
                const Icon = shortcut.icon
                return (
                  <Link
                    key={shortcut.path}
                    href={shortcut.path}
                    data-shortcut={shortcut.path}
                    title={shortcut.label}
                    aria-label={shortcut.label}
                    className={cn(
                      'hidden md:flex p-2 rounded-lg transition-colors',
                      shortcut.tone,
                    )}
                  >
                    <Icon className="w-4 h-4" />
                  </Link>
                )
              },
            )}
            <NotificationBell />
            <LanguageMenu />
            {/* The profile and sign-out moved into the sidebar footer - §9.7.3. The header keeps what
                you change mid-shift rather than at the end of one. */}
          </div>
        </header>

        <main className="p-4 sm:p-6">{children}</main>
      </div>
    </div>
  )
}

/** Holds the app back until the session is known, so nothing flashes. */
export function AuthGate({ children }: { children: ReactNode }) {
  const { user, isLoading } = useAuth()
  const [, navigate] = useLocation()

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Spinner />
      </div>
    )
  }

  if (!user) {
    navigate('/login', { replace: true })
    return null
  }

  return <>{children}</>
}
